'use client';

import { useState, useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { Languages } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  locales,
  localeNames,
  defaultLocale,
  normalizeLocale,
  LANGUAGE_STORAGE_KEY,
  resolveLandingLocale,
  replaceLocalePrefixInPathname,
  type Locale,
} from '@/i18n/config';

export function LandingLanguageSwitcher() {
  const [currentLocale, setCurrentLocale] = useState<Locale>(defaultLocale);
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    function sync() {
      setCurrentLocale(resolveLandingLocale(pathname));
    }
    sync();
    window.addEventListener('languageChange', sync);
    return () => window.removeEventListener('languageChange', sync);
  }, [pathname]);

  const handleLanguageChange = (locale: Locale) => {
    setCurrentLocale(locale);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, locale);

    const nextPath = replaceLocalePrefixInPathname(pathname || '/', locale);
    router.replace(nextPath);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon">
          <Languages className="h-[1.2rem] w-[1.2rem]" />
          <span className="sr-only">Toggle language</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {locales.map((locale) => (
          <DropdownMenuItem
            key={locale}
            onClick={() => handleLanguageChange(locale)}
            className={`cursor-pointer ${locale === currentLocale ? 'font-bold bg-accent' : ''
              }`}
          >
            {localeNames[locale]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** URL segment for links (e.g. `pt-br`), synced with pathname + localStorage + language switcher */
export function usePreferredLanguage(): string {
  const pathname = usePathname();
  const [locale, setLocale] = useState<string>(() =>
    normalizeLocale(defaultLocale),
  );

  useEffect(() => {
    function sync() {
      setLocale(normalizeLocale(resolveLandingLocale(pathname)));
    }

    sync();
    window.addEventListener('languageChange', sync);
    return () => window.removeEventListener('languageChange', sync);
  }, [pathname]);

  return locale;
}
